<template>
  <div v-if="video && videoUrl" id="video-player">
    <media-video
      :video-src="videoUrl"
      :transcript-source="transcriptSource"
      :video-id="moduleVideoId || video.id"
      :aria-label="`Video player for ${video.name}`"
      :has-transcript="hasTranscript"
      @ready="markVideoReady"
    />
    <div class="video-details-container">
      <h2 class="video-title">
        <i class="fas fa-film" aria-hidden="true"></i>
        {{ video.name }}
      </h2>
      <span class="current-video-date">
        <small>{{ formattedDate }}</small>
      </span>
      <p class="current-video-description">{{ video.description }}</p>
    </div>
  </div>
</template>

<style scoped>
.video-title {
  width: 100%;
  margin-bottom: 15px;
}

.video-details-container {
  border-left: 3px solid var(--kate-background-body);
  text-align: left;
  padding: 25px 25px 5px;
}

.video-details-container h2 {
  margin: 0;
}

.video-details-container h2 i {
  margin-right: 10px;
  color: var(--kate-primary);
}

.video-details-container .current-video-date {
  display: block;
  padding: 10px 0 0;
}

.video-details-container p {
  margin-top: 10px;
  color: var(--kate-type-light);
}
</style>

<script>
import TimeMixins from '@mixins/time-mixins';
import MediaVideo from '@base-components/media/video.vue';

export default {
  components: {
    MediaVideo,
  },

  props: {
    moduleId: {
      type: Number,
    },
    moduleVideoId: {
      type: Number,
    },
    video: {
      type: Object,
      required: true,
    },
    supportsRangeHeaders: {
      type: Boolean,
      default: null,
    },
    hasTranscript: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [TimeMixins],

  data() {
    return {
      ready: false,
      videoSignedUrl: null,
      transcriptSource: null,
    };
  },

  watch: {
    'video.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          // reset state when video changes
          this.ready = false;
          this.videoSignedUrl = null;

          // check based on headers
          if (this.supportsRangeHeaders === false) {
            this.getSignedUrl(newId);
          } else if (this.supportsRangeHeaders === true) {
            this.ready = true;
          }
          this.fetchTranscript();
        }
      },
    },
    ready() {
      this.$emit('ready', this.ready);
    },
    supportsRangeHeaders: {
      handler(newVal) {
        if (newVal === false) {
          this.getSignedUrl(this.video.id);
        } else if (newVal === true) {
          this.fetchTranscript();
          this.ready = true;
        }
      },
      immediate: true,
    },
  },

  computed: {
    videoUrl() {
      if (this.supportsRangeHeaders === false && this.videoSignedUrl) {
        return this.videoSignedUrl;
      }
      if (this.supportsRangeHeaders === true) {
        return `/api/static/video/${this.video.id}/watch`;
      }
      return null;
    },

    videoResourceLink() {
      return this.video.resource_link;
    },

    formattedDate() {
      return this.parseTimestamp(this.video.date_recorded, undefined);
    },

    transcriptApiUrl() {
      const videoId = this.video?.id;
      const modVideoId = this.moduleVideoId || this.video?.id;
      if (!videoId || !modVideoId) return null;
      return `/api/static/module/video/${modVideoId}/transcript`;
    },
  },

  methods: {
    markVideoReady() {
      this.ready = true;
    },

    getSignedUrl(moduleVideoId) {
      this.$logger.info('Getting video source with signed URL', { moduleVideoId }, true);
      this.$http.get(`/api/curriculum/video/${moduleVideoId}/watch`)
        .then(result => {
          this.$logger.info('Got video source', { moduleVideoId });
          this.videoSignedUrl = result.data.src;
          this.fetchTranscript();
        }).catch(err => {
          if (this.$http.isWarning(err)) {
            this.$logger.warn('Error getting signed URL', { moduleVideoId }, err);
            this.$ktoast.showError(err, true);
          } else {
            this.$logger.error('Error getting signed URL', { moduleVideoId }, err);
            this.$ktoast.showError(err, true);
          }
        });
    },

    fetchTranscript() {
      if (this.transcriptApiUrl) {
        this.transcriptSource = this.transcriptApiUrl;
      }
    },
  },
};
</script>
